import { isNativeApp } from '../native';

/**
 * Google Analytics 4 (GA4), optional und nur mit Einwilligung (§ 25 TDDDG, Art. 6 Abs. 1 lit. a DSGVO).
 *
 * - **Aus, solange der Server keine Mess-ID liefert** (`ANALYTICS_GA_ID` leer, siehe `GET /version`): Es gibt dann kein
 *   Banner, keinen Skript-Aufruf, kein Cookie.
 * - **Nichts vor der Einwilligung:** Das Skript `gtag.js` wird erst nach „Annehmen“ nachgeladen. „Ablehnen“ ist gleichwertig.
 * - **Widerruf jederzeit** (`/datenschutz-einstellungen`, Profil, Fußzeile): Der Aufruf stoppt, die Cookies `_ga*` werden gelöscht.
 * - **Browser-Signale** „Do Not Track“ und „Global Privacy Control“ gelten als Ablehnung: kein Banner, kein Laden.
 * - **Native App:** GA wird nicht geladen (App Tracking Transparency fehlt noch, Plan 16 / S1b).
 * - **Datensparsamkeit:** keine Werbefunktionen (`allow_google_signals`, `allow_ad_personalization_signals` aus,
 *   Consent-Modus mit „denied“ als Grundwert, Werbung immer „denied“), IP-Anonymisierung, keine Nutzerkennung. Jedes
 *   Ereignis (auch automatisch erfasste) trägt nur den Pfad ohne Abfrage und Fragment (dort stehen Suchbegriffe und
 *   Einmal-Token, CLAUDE.md Regel 10) mit ersetzten Kennungen und ohne Referrer.
 */

export type ConsentChoice = 'granted' | 'denied';

export interface AnalyticsState {
  /** Mess-ID vom Server, `null` = GA ist nicht eingeschaltet. */
  gaId: string | null;
  /** In der nativen App wird GA nie geladen. */
  native: boolean;
  /** Der Browser sendet „Do Not Track“ oder „Global Privacy Control“. */
  browserSignal: boolean;
  /** Gespeicherte Entscheidung, `null` = noch keine. */
  consent: ConsentChoice | null;
  /** GA ist auf diesem System eingeschaltet und darf grundsätzlich laufen (Web, Mess-ID vorhanden). */
  active: boolean;
  /** Das Banner ist fällig: aktiv, kein Browser-Signal, noch keine Entscheidung. */
  needsChoice: boolean;
  /** GA ist gerade geladen (Einwilligung vorhanden und kein Browser-Signal). */
  loaded: boolean;
}

/** Schlüssel der gespeicherten Entscheidung (technisch notwendig, § 25 Abs. 2 Nr. 2 TDDDG). */
export const CONSENT_KEY = 'barso.analytics.consent';
/** Nach so vielen Monaten wird die Entscheidung erneut abgefragt (die Datenschutzerklärung nennt dieselbe Zahl). */
export const CONSENT_MAX_AGE_MONTHS = 12;
export const CONSENT_MAX_AGE_DAYS = Math.round((CONSENT_MAX_AGE_MONTHS * 365) / 12);
/** Laufzeit der GA-Cookies in Monaten (die Datenschutzerklärung nennt dieselbe Zahl). */
export const GA_COOKIE_MONTHS = 13;
const COOKIE_EXPIRES_SECONDS = Math.round((GA_COOKIE_MONTHS * 365 * 24 * 60 * 60) / 12);

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
} & Record<string, unknown>;

const win = (): GtagWindow => window as unknown as GtagWindow;

let state: AnalyticsState = computeState(null, null, false, false);
let configuredId: string | null = null;
let consent: ConsentChoice | null = null;
let memoryOnly = false;
let lastPath: string | null = null;
const listeners = new Set<() => void>();

function computeState(
  gaId: string | null,
  choice: ConsentChoice | null,
  native: boolean,
  signal: boolean,
): AnalyticsState {
  const active = gaId !== null && !native;
  const effective = signal ? 'denied' : choice;
  return {
    gaId,
    native,
    browserSignal: signal,
    consent: effective,
    active,
    needsChoice: active && effective === null,
    loaded: active && effective === 'granted',
  };
}

/** „Do Not Track“ oder „Global Privacy Control“ im Browser (je eines genügt). */
export function hasBrowserPrivacySignal(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & {
    globalPrivacyControl?: boolean;
    msDoNotTrack?: string;
  };
  const dnt = nav.doNotTrack ?? nav.msDoNotTrack ?? (win()['doNotTrack'] as string | undefined);
  return dnt === '1' || dnt === 'yes' || nav.globalPrivacyControl === true;
}

function readStoredConsent(now = Date.now()): ConsentChoice | null {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { choice, at } = parsed as { choice?: unknown; at?: unknown };
    if (choice !== 'granted' && choice !== 'denied') return null;
    const time = typeof at === 'string' ? Date.parse(at) : Number.NaN;
    if (Number.isNaN(time) || now - time > CONSENT_MAX_AGE_DAYS * 86_400_000) return null;
    return choice;
  } catch {
    return null;
  }
}

function storeConsent(choice: ConsentChoice): void {
  try {
    window.localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ choice, at: new Date().toISOString() }),
    );
    memoryOnly = false;
  } catch {
    // Speicher gesperrt: Die Entscheidung gilt nur bis zum Neuladen
    memoryOnly = true;
  }
}

/** Pfad ohne Abfrage und Fragment, Kennungen (UUID, lange Zeichenfolgen, Zahlen) durch `:id` ersetzt. */
export function safePagePath(pathname: string): string {
  const path = pathname.split(/[?#]/)[0] ?? '/';
  const cleaned = path
    .split('/')
    .map((segment) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
      /^\d{4,}$/.test(segment) ||
      /^[A-Za-z0-9_-]{24,}$/.test(segment)
        ? ':id'
        : segment,
    )
    .join('/');
  return cleaned === '' ? '/' : cleaned;
}

const GA_COOKIE = /^(_ga|_gid|_gat|_gcl_)/;

/** Löscht alle GA-Cookies (`_ga`, `_ga_<ID>`, `_gid`, `_gat*`) auf dieser und den übergeordneten Domains. */
export function deleteGaCookies(): void {
  if (typeof document === 'undefined') return;
  const names = document.cookie
    .split(';')
    .map((part) => part.split('=')[0]?.trim() ?? '')
    .filter((name) => GA_COOKIE.test(name));
  if (names.length === 0) return;
  const labels = window.location.hostname.split('.');
  const domains: (string | null)[] = [null];
  for (let i = 0; i < labels.length - 1; i += 1) domains.push(`.${labels.slice(i).join('.')}`);
  domains.push(window.location.hostname);
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=/${
        domain === null ? '' : `; domain=${domain}`
      }`;
    }
  }
}

function scriptElement(): HTMLScriptElement | null {
  return document.querySelector<HTMLScriptElement>('script[data-barso-ga]');
}

/** `gtag.js` wurde in dieser Seite schon einmal ausgeführt (bleibt im Speicher, auch wenn das Element entfernt ist). */
let scriptInjected = false;
/** Grundeinstellungen (Consent-Modus „denied“, Werbung aus) sind im `dataLayer` gesetzt. */
let defaultsPushed = false;

/**
 * Adresse, die GA für **jedes** Ereignis verwendet (auch für automatisch erfasste wie „erweiterte Messung“ bei
 * Verlaufswechseln, Scrollen oder Klicks): ohne Abfrage und Fragment, Kennungen ersetzt (Regel 10). Ohne diese
 * Vorgabe nähme gtag.js `document.location` mit Suchbegriffen aus Filtern und Einmal-Token.
 */
function setSafeLocation(gtag: (...args: unknown[]) => void, path: string): void {
  gtag('set', {
    page_location: `${window.location.origin}${path}`,
    page_path: path,
    page_referrer: '',
  });
}

function loadGa(id: string): void {
  const w = win();
  w[`ga-disable-${id}`] = false;
  // Immer dasselbe Array: Ein schon ausgeführtes gtag.js liest nur das Array, das es beim Start vorgefunden hat
  w.dataLayer ??= [];
  const layer = w.dataLayer;
  w.gtag = function gtag() {
    // gtag.js erwartet das Objekt `arguments`, kein Array
    // eslint-disable-next-line prefer-rest-params
    layer.push(arguments);
  };
  const gtag = w.gtag;
  if (!defaultsPushed) {
    // Consent-Modus: alles „denied“ als Grundwert, Werbung bleibt immer aus
    gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    });
    gtag('set', 'ads_data_redaction', true);
    gtag('js', new Date());
    defaultsPushed = true;
  }
  // Die Einwilligung gilt nur für die Reichweitenmessung
  gtag('consent', 'update', { analytics_storage: 'granted' });
  const path = safePagePath(window.location.pathname);
  setSafeLocation(gtag, path);
  gtag('config', id, {
    send_page_view: false,
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: `${window.location.origin}${path}`,
    page_referrer: '',
    cookie_expires: COOKIE_EXPIRES_SECONDS,
    cookie_flags: 'SameSite=Lax;Secure',
  });
  // Nach Widerruf und erneutem Annehmen in derselben Seite nicht noch einmal ausführen: Das erste gtag.js ist noch im
  // Speicher, liest dasselbe Array und läuft nach `ga-disable-<ID>` = false und der Einwilligung weiter
  if (!scriptInjected) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    script.dataset['barsoGa'] = '1';
    document.head.appendChild(script);
    scriptInjected = true;
  }
  lastPath = null;
  trackPageView(window.location.pathname);
}

function unloadGa(id: string): void {
  const w = win();
  // Erst dem laufenden gtag.js die Einwilligung entziehen, dann der Schalter, der es bis zum Neuladen anhält
  w.gtag?.('consent', 'update', { analytics_storage: 'denied' });
  w[`ga-disable-${id}`] = true;
  scriptElement()?.remove();
  delete w.gtag;
  lastPath = null;
  deleteGaCookies();
}

/** Kennung, mit der GA gerade geladen ist (`null` = nicht geladen). */
let loadedId: string | null = null;

function apply(): void {
  const native = configuredId !== null && isNativeApp();
  state = computeState(configuredId, consent, native, hasBrowserPrivacySignal());
  if (state.loaded && configuredId !== null && loadedId === null) {
    loadedId = configuredId;
    loadGa(configuredId);
  } else if (!state.loaded && loadedId !== null) {
    unloadGa(loadedId);
    loadedId = null;
  }
  for (const listener of [...listeners]) listener();
}

/** Setzt die Mess-ID des Servers (`null` = GA aus). Einmal beim Start, nach `GET /version`. */
export function configureAnalytics(gaId: string | null): void {
  configuredId = gaId;
  consent = gaId === null ? null : readStoredConsent();
  apply();
  // Reste einer früheren Einwilligung (abgelaufen, jetzt Browser-Signal, abgelehnt oder GA inzwischen abgeschaltet)
  // nicht stehen lassen
  if (!state.loaded) deleteGaCookies();
}

/** „Annehmen“: speichert die Einwilligung und lädt GA. Ohne Mess-ID, in der App oder bei Browser-Signal ohne Wirkung. */
export function acceptAnalytics(): void {
  if (configuredId === null || state.native || state.browserSignal) return;
  consent = 'granted';
  storeConsent('granted');
  apply();
}

/** „Ablehnen“ und zugleich Widerruf: speichert die Ablehnung, stoppt GA und löscht die Cookies. */
export function declineAnalytics(): void {
  if (configuredId === null) return;
  consent = 'denied';
  storeConsent('denied');
  apply();
}

/** Seitenaufruf melden (nur wenn geladen). Doppelte Folgemeldungen derselben Seite werden unterdrückt. */
export function trackPageView(pathname: string): void {
  const w = win();
  if (!state.loaded || w.gtag === undefined) return;
  const path = safePagePath(pathname);
  if (path === lastPath) return;
  lastPath = path;
  setSafeLocation(w.gtag, path);
  w.gtag('event', 'page_view', {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
  });
}

export function getAnalyticsState(): AnalyticsState {
  return state;
}

export function subscribeAnalytics(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Die Entscheidung gilt nur bis zum Neuladen (Speicher des Browsers gesperrt). */
export function isConsentMemoryOnly(): boolean {
  return memoryOnly;
}

/** Nur für Tests: Zustand zurücksetzen. */
export function resetAnalyticsForTests(): void {
  if (loadedId !== null) unloadGa(loadedId);
  loadedId = null;
  configuredId = null;
  consent = null;
  memoryOnly = false;
  lastPath = null;
  scriptInjected = false;
  defaultsPushed = false;
  delete win().dataLayer;
  state = computeState(null, null, false, false);
  listeners.clear();
}
